import { RegionShared } from "@1games-ru/region-shared";
import { Phase } from "@1games-ru/region-shared/dist/enums/death/phase";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTimer } from "react-timer-hook";
import rpc from "@1games-ru/rage-rpc";
import DeathScreenMpProvider from "@/core/data-provider/data-mp/death-screen/death-screen-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { KeyAction } from "@/shared/ui/KeyActionPanel";
import { IS_DEV } from "@/domains/main/constants/other.ts";
import KeyCode from "@/assets/constants/key-code";

export default () => {
  const dispatch = useAppDispatch();
  const [isWaitingAmbulance, setIsWaitingAmbulance] = useState(false);
  const [ambulanceIsComing, setAmbulanceIsComing] = useState(false);
  const [deathState, setDeathState] = useState<null | typeof RegionShared.Types.Death.State>(
    IS_DEV
      ? {
          respawnTime: new Date(Number(new Date()) + 20 * 1000), // Example using the current date and time
          deathTime: new Date(),
          phase: RegionShared.Enums.Death.Phase.CriticalCondition,
          damages: [
            {
              name: "Владимир Путин",
              id: "1",
              hp: -36, // Example damage value
              type: "Лоукик в голову", // Example damage type
            },
            {
              name: "Наталья Морская Пехота",
              id: "421",
              hp: -64, // Example damage value
              type: "Сбила на джипе из Москвы", // Example damage type
            },
          ],
        }
      : null,
  );
  const isInitDeathState = useMemo(() => Boolean(deathState), [deathState]);
  const { minutes, seconds, restart, pause, resume } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: async () => {
      if (deathState.phase === RegionShared.Enums.Death.Phase.CriticalCondition) {
        if (IS_DEV) {
          setDeathState((state) => ({
            ...state,
            phase: RegionShared.Enums.Death.Phase.Unconscious,
            respawnTime: new Date(Number(new Date()) + 10 * 1000),
          }));
        } else {
          pause();
          const response = await DeathScreenMpProvider.setPhase(Phase.Unconscious);
          if (response instanceof RpcMpProviderSuccessResponse) {
            resume();
          } else {
            // TODO:
          }
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (IS_DEV) {
          setDeathState(null);
        } else {
          pause();
          const response = await DeathScreenMpProvider.timeExpired();
          if (response instanceof RpcMpProviderSuccessResponse) {
            resume();
            setDeathState(null);
          } else {
            // TODO:
          }
        }
      }
    },
  });

  useEffect(() => {
    if (deathState) {
      restart(new Date(deathState.respawnTime));
    }
  }, [deathState, restart]);

  useEffect(() => {
    if (!isInitDeathState) {
      setAmbulanceIsComing(false);
      setIsWaitingAmbulance(false);
    }
  }, [isInitDeathState]);

  useLayoutEffect(() => {
    rpc.register(
      RegionShared.Cef.Events.Death.Show,
      (state: typeof RegionShared.Cef.Requests.DeathShowRequest): void => {
        setDeathState(state);
      },
    );

    rpc.register(RegionShared.Cef.Events.Death.Hide, (): void => {
      setDeathState(null);
    });

    return () => {
      rpc.unregister(RegionShared.Cef.Events.Death.Show);
      rpc.unregister(RegionShared.Cef.Events.Death.Hide);
    };
  }, [dispatch]);

  const actions = useMemo(() => {
    const keysActions = new Map<KeyCode, KeyAction>();

    keysActions.set(KeyCode.E, {
      label: "В больницу",
      keyName: "E",
      color: "red",
      action: async () => {
        if (IS_DEV) {
          setDeathState(null);
        } else {
          pause();
          const response = await DeathScreenMpProvider.toHospital();
          if (response instanceof RpcMpProviderSuccessResponse) {
            resume();
            setDeathState(null);
          }
        }
      },
    });

    keysActions.set(KeyCode.F, {
      label: "Антишок",
      keyName: "F",
      color: "red",
      action: async () => {
        if (IS_DEV) {
          setDeathState(null);
        } else {
          pause();
          const response = await DeathScreenMpProvider.getAntiShock();
          if (response instanceof RpcMpProviderSuccessResponse) {
            resume();
          }
        }
      },
    });

    keysActions.set(KeyCode.I, {
      label: "Жалоба",
      keyName: "I",
      color: "red",
      action: () => {
        // TODO
      },
    });

    if (!isWaitingAmbulance) {
      keysActions.set(KeyCode.G, {
        label: "Ждать скорую",
        keyName: "G",
        color: "red",
        action: async () => {
          setIsWaitingAmbulance(true);
          if (!IS_DEV) {
            pause();
            const response = await DeathScreenMpProvider.waitAmbulance();
            if (response instanceof RpcMpProviderSuccessResponse) {
              resume();
            }
          }
        },
      });
    }

    return keysActions;
  }, [isWaitingAmbulance, pause, resume, setDeathState]);

  return {
    isShow: deathState,
    deathState,
    setDeathState,
    isWaitingAmbulance,
    ambulanceIsComing,
    minutes,
    seconds,
    actions,
  };
};
